import { type ApiEnvelope, api, unwrap } from "@/lib/api";

export type NewsletterStatus =
	| "draft"
	| "scheduled"
	| "sending"
	| "sent"
	| "paused"
	| "failed";

export const BLOCK_TYPES = [
	"header",
	"text",
	"products",
	"banner",
	"review",
	"article",
	"button",
] as const;
export type NewsletterBlockType = (typeof BLOCK_TYPES)[number];

export interface NewsletterBlock {
	type: NewsletterBlockType;
	title?: string;
	body?: string;
	url?: string;
	image?: string;
	productIds?: string[];
	articleId?: string;
	code?: string;
	color?: string;
}

export interface Campaign {
	id: string;
	name: string;
	subject: string;
	preheader: string | null;
	segmentId: string | null;
	segmentLabel: string | null;
	status: NewsletterStatus;
	scheduledAt: string | null;
	startedAt: string | null;
	finishedAt: string | null;
	recipients: number;
	sentCount: number;
	failedCount: number;
	openCount: number;
	clickCount: number;
	openRate: number;
	clickRate: number;
	approvalRequired: boolean;
	approvedAt: string | null;
	lastError: string | null;
	updatedAt: string;
	createdAt: string;
}

export interface CampaignDetail extends Campaign {
	blocks: NewsletterBlock[];
}

export interface NewsletterStats {
	sent: number;
	scheduled: number;
	draft: number;
	subscribers: number;
	openRate: number;
}

export interface CampaignReport {
	campaign: Campaign;
	sent: number;
	opened: number;
	clicked: number;
	failed: number;
	openRate: number;
	clickRate: number;
}

export const newslettersApi = {
	list: (params?: { status?: NewsletterStatus; page?: number; pageSize?: number }) =>
		unwrap<{ items: Campaign[]; total: number }>(
			api.get<ApiEnvelope<{ items: Campaign[]; total: number }>>(
				"/admin/newsletters/campaigns",
				{ params },
			),
		),

	stats: () =>
		unwrap<NewsletterStats>(
			api.get<ApiEnvelope<NewsletterStats>>("/admin/newsletters/stats"),
		),

	estimate: (segmentId?: string | null) =>
		unwrap<{ recipients: number; approvalRequired: boolean }>(
			api.get<ApiEnvelope<{ recipients: number; approvalRequired: boolean }>>(
				"/admin/newsletters/estimate",
				{ params: segmentId ? { segmentId } : undefined },
			),
		),

	detail: (id: string) =>
		unwrap<CampaignDetail>(
			api.get<ApiEnvelope<CampaignDetail>>(
				`/admin/newsletters/campaigns/${id}`,
			),
		),

	report: (id: string) =>
		unwrap<CampaignReport>(
			api.get<ApiEnvelope<CampaignReport>>(
				`/admin/newsletters/campaigns/${id}/report`,
			),
		),

	/** Rendu HTML d'un jeu de blocs — l'aperçu de l'éditeur. */
	preview: (payload: {
		blocks: NewsletterBlock[];
		subject?: string;
		preheader?: string;
	}) =>
		unwrap<{ html: string }>(
			api.post<ApiEnvelope<{ html: string }>>(
				"/admin/newsletters/preview",
				payload,
			),
		),

	create: (payload: {
		name: string;
		subject: string;
		preheader?: string;
		segmentId?: string;
		blocks?: NewsletterBlock[];
	}) =>
		unwrap<CampaignDetail>(
			api.post<ApiEnvelope<CampaignDetail>>(
				"/admin/newsletters/campaigns",
				payload,
			),
		),

	update: (
		id: string,
		payload: {
			name?: string;
			subject?: string;
			preheader?: string;
			segmentId?: string | null;
			blocks?: NewsletterBlock[];
		},
	) =>
		unwrap<CampaignDetail>(
			api.patch<ApiEnvelope<CampaignDetail>>(
				`/admin/newsletters/campaigns/${id}`,
				payload,
			),
		),

	schedule: (id: string, at?: string) =>
		unwrap<CampaignDetail>(
			api.post<ApiEnvelope<CampaignDetail>>(
				`/admin/newsletters/campaigns/${id}/schedule`,
				at ? { at } : {},
			),
		),

	approve: (id: string, reject = false) =>
		unwrap<CampaignDetail>(
			api.post<ApiEnvelope<CampaignDetail>>(
				`/admin/newsletters/campaigns/${id}/approve`,
				{ reject },
			),
		),

	cancel: (id: string) =>
		unwrap<CampaignDetail>(
			api.post<ApiEnvelope<CampaignDetail>>(
				`/admin/newsletters/campaigns/${id}/cancel`,
			),
		),

	duplicate: (id: string) =>
		unwrap<CampaignDetail>(
			api.post<ApiEnvelope<CampaignDetail>>(
				`/admin/newsletters/campaigns/${id}/duplicate`,
			),
		),

	test: (id: string, email: string) =>
		unwrap<{ sent: boolean; to: string }>(
			api.post<ApiEnvelope<{ sent: boolean; to: string }>>(
				`/admin/newsletters/campaigns/${id}/test`,
				{ email },
			),
		),

	remove: (id: string) =>
		unwrap<{ deleted: boolean }>(
			api.delete<ApiEnvelope<{ deleted: boolean }>>(
				`/admin/newsletters/campaigns/${id}`,
			),
		),
};

export const NEWSLETTER_STATUS_LABELS: Record<
	NewsletterStatus,
	{ label: string; variant: "success" | "info" | "neutral" | "warning" | "danger" }
> = {
	sent: { label: "Envoyée", variant: "success" },
	sending: { label: "En cours d'envoi", variant: "info" },
	scheduled: { label: "Programmée", variant: "info" },
	draft: { label: "Brouillon", variant: "neutral" },
	paused: { label: "Suspendue", variant: "warning" },
	failed: { label: "Échouée", variant: "danger" },
};

/** Ce que chaque brique met dans l'email, en clair. */
export const BLOCK_LABELS: Record<
	NewsletterBlockType,
	{ name: string; desc: string; icon: "layout" | "edit" | "box" | "tag" | "star" | "chart" | "arrow" }
> = {
	header: {
		name: "En-tête",
		desc: "Bandeau terracotta avec le logo et la salutation",
		icon: "layout",
	},
	text: { name: "Texte", desc: "Paragraphe libre", icon: "edit" },
	products: {
		name: "Produits",
		desc: "Photo, prix et lien pour chaque produit choisi",
		icon: "box",
	},
	banner: {
		name: "Bannière promo",
		desc: "Message en pleine largeur avec code promo",
		icon: "tag",
	},
	review: { name: "Avis client", desc: "Témoignage cinq étoiles", icon: "star" },
	article: { name: "Article du Mag", desc: "Renvoi vers un article", icon: "chart" },
	button: { name: "Bouton", desc: "Appel à l'action centré", icon: "arrow" },
};
